import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Grid, Typography, Box, Card, CardHeader, CardContent, Divider,
    IconButton, Tooltip, TextField, Switch, FormControlLabel,
    Alert, Skeleton
} from '@mui/material';
import { ContentCopy, ArrowBack } from '@mui/icons-material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { fetchFmDataProduct, fetchFmAvailableDates } from '../../lib/services/fmDataService';
import { getSettings } from '../../lib/services/settings';

const ApiHubProductPage = ({ idToken, drawerWidth }) => {
    const location = useLocation();
    const navigate = useNavigate();

    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [availableDates, setAvailableDates] = useState([]);
    const [queryParams, setQueryParams] = useState({});
    const [getLatest, setGetLatest] = useState(true);
    const [selectedDate, setSelectedDate] = useState(null);
    const [url, setUrl] = useState('');
    const [fmBaseUrl, setFmBaseUrl] = useState('');
    const [copied, setCopied] = useState(false);

    // Load settings for URL display
    useEffect(() => {
        getSettings().then(s => setFmBaseUrl(s.fmBaseUrl || ''));
    }, []);

    // Load product by ID via dedicated API call
    useEffect(() => {
        const productId = location.state?.productId;
        if (!idToken || !productId) {
            setError('No product ID provided. Please select a product from the API Hub list.');
            setLoading(false);
            return;
        }
        setLoading(true);
        fetchFmDataProduct(idToken, productId)
            .then((response) => {
                if (!response.isError) {
                    const found = response.data;
                    setProduct(found);
                    // Initialize query params from the returned product
                    const params = {};
                    (found.dataProductQueryParameters || []).forEach(qp => {
                        params[qp.name] = qp.suggestedValue || '';
                    });
                    setQueryParams(params);
                } else {
                    setError(response.data || 'Failed to load data product');
                }
            })
            .catch((e) => setError(e.message))
            .finally(() => setLoading(false));
    }, [idToken, location.state]);

    // Load available dates
    useEffect(() => {
        if (!product || !idToken) return;
        fetchFmAvailableDates(idToken, product.id)
            .then((response) => {
                if (!response.isError && Array.isArray(response.data)) {
                    setAvailableDates(response.data);
                }
            })
            .catch(() => { });
    }, [product, idToken]);

    // Build URL
    useEffect(() => {
        if (!product) return;
        const alias = product.userFriendlyAliasName || product.name;
        let builtUrl = `${fmBaseUrl}/api/fm/data/${encodeURIComponent(alias)}`;
        const paramParts = [];

        (product.dataProductQueryParameters || []).forEach(qp => {
            const name = qp.name;
            const value = queryParams[name] || '';
            if (name.toLowerCase() === 'date' && getLatest) return;
            if (name.toLowerCase() === 'getlatest') {
                paramParts.push(`${name}=${getLatest}`);
                return;
            }
            paramParts.push(`${name}=${value}`);
        });

        if (paramParts.length > 0) {
            builtUrl += '?' + paramParts.join('&');
        }
        setUrl(builtUrl);
    }, [product, queryParams, getLatest, fmBaseUrl]);

    const handleCopyUrl = () => {
        navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleParamChange = (name, value) => {
        setQueryParams(prev => ({ ...prev, [name]: value }));
    };

    const formatDateForQuery = (date) => {
        if (!date || isNaN(date.getTime())) return '';
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const contentOffset = Math.max((drawerWidth || 0) - 15, 0);
    const containerStyle = {
        marginLeft: contentOffset,
        width: `calc(100% - ${contentOffset}px)`,
        boxSizing: 'border-box'
    };

    if (loading) {
        return (
            <Grid container spacing={2} style={containerStyle}>
                <Grid item xs={12}>
                    <Skeleton variant="rectangular" height={60} sx={{ mb: 2 }} />
                    <Skeleton variant="rectangular" height={300} />
                </Grid>
            </Grid>
        );
    }

    if (error) {
        return (
            <Grid container spacing={2} style={containerStyle}>
                <Grid item xs={12}>
                    <Alert severity="error">{error}</Alert>
                </Grid>
            </Grid>
        );
    }

    if (!product) return null;

    const queryParameters = product.dataProductQueryParameters || [];
    const groups = (product.permissionRules || [])
        .flatMap(r => r.groups || [])
        .filter((v, i, a) => a.indexOf(v) === i);

    return (
        <Grid container spacing={2} style={containerStyle}>
            <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <IconButton onClick={() => navigate('/apihub')} sx={{ mr: 1 }}>
                        <ArrowBack />
                    </IconButton>
                    <Typography variant="h5">
                        {product.userFriendlyAliasName || product.name}
                    </Typography>
                </Box>
                <Typography color="text.secondary" sx={{ mb: 1 }}>
                    {product.description}
                </Typography>
                {groups.length > 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Members of the following groups can access this data: {groups.join(', ')}
                    </Typography>
                )}
            </Grid>

            {/* Configure Query Card */}
            <Grid item xs={12}>
                <Card elevation={3} sx={{ maxWidth: 1100, width: '100%', mx: 'auto' }}>
                    <CardHeader title="Configure your query" />
                    <CardContent>
                        <Grid container spacing={2} alignItems="center">
                            {queryParameters.map((qp, i) => {
                                const isDate = qp.name.toLowerCase() === 'date';
                                const isGetLatest = qp.name.toLowerCase() === 'getlatest';
                                const isDisabled = isDate && getLatest;

                                if (isGetLatest) {
                                    return (
                                        <Grid item xs={12} sm={6} key={i}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={getLatest}
                                                        onChange={(e) => setGetLatest(e.target.checked)}
                                                        color="primary"
                                                    />
                                                }
                                                label="Get Latest"
                                            />
                                        </Grid>
                                    );
                                }

                                if (isDate) {
                                    return (
                                        <Grid item xs={12} sm={6} key={i}>
                                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                                <DatePicker
                                                    label={qp.name}
                                                    value={selectedDate}
                                                    onChange={(val) => {
                                                        setSelectedDate(val);
                                                        const formatted = formatDateForQuery(val);
                                                        handleParamChange(qp.name, formatted);
                                                    }}
                                                    disabled={isDisabled}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            fullWidth
                                                            size="small"
                                                            helperText={qp.description || ''}
                                                        />
                                                    )}
                                                    inputFormat="dd/MM/yyyy"
                                                />
                                            </LocalizationProvider>
                                        </Grid>
                                    );
                                }

                                if (qp.parameterType === 'Boolean') {
                                    return (
                                        <Grid item xs={12} sm={6} key={i}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={queryParams[qp.name] === 'true'}
                                                        onChange={(e) => handleParamChange(qp.name, e.target.checked ? 'true' : 'false')}
                                                        color="primary"
                                                    />
                                                }
                                                label={qp.name}
                                            />
                                        </Grid>
                                    );
                                }

                                return (
                                    <Grid item xs={12} sm={6} key={i}>
                                        <TextField
                                            label={qp.name}
                                            value={queryParams[qp.name] || ''}
                                            onChange={(e) => handleParamChange(qp.name, e.target.value)}
                                            fullWidth
                                            size="small"
                                            helperText={qp.description || ''}
                                        />
                                    </Grid>
                                );
                            })}
                        </Grid>

                        <Divider sx={{ my: 2 }} />

                        <Typography variant="h6" sx={{ mb: 1 }}>Your Query URL</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', minWidth: 0 }}>
                            <Typography
                                variant="body1"
                                sx={{ wordBreak: 'break-all', color: 'primary.main', minWidth: 0 }}
                                component="a"
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {url}
                            </Typography>
                            <Tooltip title={copied ? "Copied!" : "Copy URL to clipboard"}>
                                <IconButton onClick={handleCopyUrl} size="small">
                                    <ContentCopy color="primary" fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </CardContent>
                </Card>
            </Grid>

        </Grid>
    );
};

export default ApiHubProductPage;
